import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { Upload, message } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd/es/upload";
import axiosInstance from "../../api/axiosInstance";
import "../file-input/FileUpload.scss";

interface FileUploadProps {
    unit: string;
    type: string;
    width?: number | string;
    height?: number | string;
    expectedWidth?: number;
    expectedHeight?: number;
    initialFilePath?: string;
    onUploadSuccess?: (path: string) => void;
    onFileSelected?: () => void;
}

export interface FileUploadRef {
    uploadFile: () => Promise<string | null>;
}

const storageUrl = process.env.REACT_APP_API_STORAGE_URL;

const normalizeUrl = (url: string) => {
    return url.startsWith("/files") ? "https://storage.vnpa.vn" + url : url;
}

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
    ({ unit, type, initialFilePath, onUploadSuccess, onFileSelected, width, height, expectedWidth, expectedHeight }, ref) => {
        const [file, setFile] = useState<File | null>(null);
        const [preview, setPreview] = useState<string | null>(null);

        useEffect(() => {
            if (initialFilePath) {
                setPreview(normalizeUrl(initialFilePath));
            } else {
                setPreview(null);
            }
        }, [initialFilePath]);

        const props: UploadProps = {
            showUploadList: false,
            // beforeUpload: (file) => {
            //     setFile(file);
            //     setPreview(URL.createObjectURL(file));
            //     onFileSelected?.();
            //     return false;
            // },
            beforeUpload: (file) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                        if (expectedWidth && expectedHeight) {
                            if (img.width === expectedWidth && img.height === expectedHeight) {
                                setFile(file);
                                setPreview(URL.createObjectURL(file));
                                onFileSelected?.();
                                resolve(false);
                            } else {
                                message.error(
                                    `Kích thước ảnh phải là ${expectedWidth}x${expectedHeight}. Ảnh tải lên kích thước: ${img.width}x${img.height}`
                                );
                                reject("Invalid image size");
                            }
                        } else {
                            setFile(file);
                            setPreview(URL.createObjectURL(file));
                            onFileSelected?.();
                            resolve(false);
                        }
                    };
                    img.onerror = () => {
                        message.error("Không thể đọc ảnh. Vui lòng chọn lại.");
                        reject("Image load error");
                    };
                    img.src = URL.createObjectURL(file);
                });
            },
        };

        const uploadFile = async (): Promise<string | null> => {
            if (!file) {
                return preview;
            }

            const formData = new FormData();
            formData.append("file", file);

            try {
                const response = await axiosInstance.post(
                    `${storageUrl}/files/${unit}/${type}`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                const newFilePath = response.data.path;
                const fullPath = `${storageUrl}${newFilePath}`;
                setPreview(fullPath);
                setFile(null);

                onUploadSuccess?.(newFilePath);
                return newFilePath;
            } catch (error) {
                message.error("Tải ảnh lên thất bại! Vui lòng thử lại.");
                return null;
            }
        };

        useImperativeHandle(ref, () => ({ uploadFile }));

        return (
            <Upload.Dragger
                {...props}
                className="custom-upload"
                style={{
                    width,
                    height,
                    padding: 0,
                    overflow: "hidden"
                }}
            >
                {preview ? (
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <img
                            src={preview}
                            alt="Preview"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block"
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <CameraOutlined style={{ fontSize: 32, color: "#888" }} />
                        <p style={{ color: "#888", marginTop: 8 }}>Tải lên hình ảnh</p>
                    </>
                )}
            </Upload.Dragger>

        );
    }
);

FileUpload.displayName = "FileUpload";

export default FileUpload;
